import {
  Button,
  Progress,
  Text,
  Tooltip,
  useComputedColorScheme,
  useMantineTheme,
} from "@mantine/core";
import FlagRounded from "@mui/icons-material/FlagRounded";
import FormatClearRounded from "@mui/icons-material/FormatClearRounded";
import { TestStates } from "@tsTypes/test-schema";
import { useRouter } from "next/navigation";
import { Dispatch, SetStateAction } from "react";
import { useProgressBarStyles } from "./TestPerformanceSummary";

export default function QuestionQuickJumpCard({
  index,
  currentQuestion,
  setCurrentQuestion,
  isAttempted,
  isMarked,
  isFlagged,
  testStatus,
  percentagePerformance,
  isMcQuestion,
}: {
  index: number;
  currentQuestion: number;
  setCurrentQuestion: Dispatch<SetStateAction<number>>;
  isAttempted: boolean;
  isMarked: boolean;
  isFlagged: boolean;
  testStatus: TestStates;
  percentagePerformance?: number;
  isMcQuestion: boolean;
}) {
  const router = useRouter();
  const { classes: progressBarClasses } = useProgressBarStyles();
  const theme = useMantineTheme();
  const colorScheme = useComputedColorScheme("light");
  const isDark = colorScheme === "dark";
  const showUnansweredTooltip =
    !isAttempted && (testStatus === "marking" || testStatus === "reviewing");
  const showFlaggedTooltip =
    isFlagged && (testStatus === "marking" || testStatus === "reviewing");
  const buttonStyle = getTestQuestionStatusBadgeStyle(
    isDark,
    testStatus,
    isMarked,
    isAttempted
  );
  const iconPosition = {
    top: 0,
    right: 0,
    height: 13,
    width: 13,
    position: "absolute" as any,
  };

  const borderColor =
    currentQuestion === index
      ? isDark
        ? theme.colors.lightblue[5]
        : theme.colors.darkblue[5]
      : isDark
      ? theme.colors.dark[4]
      : `color-mix(in srgb, ${theme.colors.dark[0]} 20%, white 80%)`;

  return (
    <Tooltip
      label={getTooltipText(showUnansweredTooltip, showFlaggedTooltip)}
      color="dark.6"
      disabled={!showUnansweredTooltip && !showFlaggedTooltip}
    >
      <Button
        size="compact-md"
        name={`Question:${index}`}
        variant={buttonStyle.variant}
        color={buttonStyle.color}
        pos={"relative"}
        w={70}
        styles={{
          label: { justifyContent: "center" },
          root: {
            overflow: "hidden",
            borderColor,
            boxShadow: isDark
              ? "var(--mantine-shadow-xs)"
              : "var(--mantine-shadow-sm)",
            pointerEvents: testStatus === "paused" ? "none" : undefined,
          },
        }}
        onClick={() => {
          setCurrentQuestion(index);
          router.push(`#test-metrics`);
        }}
      >
        {index + 1}
        {isFlagged && (
          <FlagRounded
            style={{
              color:
                (isAttempted && testStatus === "taking") ||
                (isMarked && testStatus === "marking")
                  ? "white"
                  : theme.colors.dark[2],
              ...iconPosition,
              left: 0,
              right: "unset",
            }}
          />
        )}
        {showUnansweredTooltip && (
          <FormatClearRounded
            style={{
              color:
                isMarked && testStatus === "marking"
                  ? "white"
                  : theme.colors.dark[2],
              ...iconPosition,
            }}
          />
        )}
        {!isMcQuestion && (
          <Text
            p="xs"
            size={"10px"}
            style={{
              position: "absolute",
              left: 0,
              bottom: 0,
              lineHeight: 1,
              fontWeight: 600,
            }}
          >
            LA
          </Text>
        )}
        {percentagePerformance !== undefined && (
          <Progress.Root
            size={"sm"}
            classNames={{
              root: progressBarClasses.progressBarRoot,
            }}
          >
            <Progress.Section
              value={percentagePerformance}
              color="correct"
              className={progressBarClasses.progressBar}
            />
            <Progress.Section
              value={100 - percentagePerformance}
              color="incorrect"
              className={progressBarClasses.progressBar}
            />
          </Progress.Root>
        )}
      </Button>
    </Tooltip>
  );
}

export const getTestQuestionStatusBadgeStyle = (
  isDark: boolean,
  testStatus: TestStates,
  isMarked: boolean,
  isAttempted: boolean
) => {
  const baseColor = isDark ? "lightblue" : "darkblue";
  if (testStatus === "marking") {
    return {
      color: isMarked ? baseColor : "dark",
      variant: isMarked ? "filled" : "white",
    };
  } else if (testStatus === "reviewing") {
    return {
      color: baseColor,
      variant: "white",
    };
  } else {
    return {
      color: baseColor,
      variant: isAttempted ? "filled" : "outline",
    };
  }
};

function getTooltipText(
  showUnansweredTooltip: boolean,
  showFlaggedTooltip: boolean
) {
  if (showUnansweredTooltip && showFlaggedTooltip) {
    return "Flagged and Unanswered";
  } else if (showUnansweredTooltip) {
    return "Unanswered";
  } else if (showFlaggedTooltip) {
    return "Flagged";
  }
  return "";
}
